// @flow
import React, {useState} from 'react';
import type {Node} from 'react';
import {Link} from '@homeaway/react-localization-components';
import {
    AspectRatioCard,
    AspectRatioCardImage
} from '@homeaway/react-aspect-ratio-card';
import {TripBoardButton} from '@homeaway/react-trip-board-button';
import {FormattedMessage} from 'react-intl';
import PriceSummary from '@homeaway/react-price-summary';
import {Waypoint} from '@homeaway/react-unveil';
import Rating from '../../Rating/Rating';
import type {PriceSummaryShape} from '../../Price/Price';
import {
    triggerSelectedSerpCarouselCardEvent,
    triggerPresentedSerpCarouselCardEvent
} from '../../../utils/collectionEdapUtils';

import './PropertyCard.less';

type PropertyCardImagesShape = {
    uri: string
};

type PropertyCardMetadataShape = {
    headline: string
};

export type PropertyCardListingShape = {
    averageRating: number,
    reviewCount: number,
    listingId: string,
    detailPageUrl: string,
    bedrooms: number,
    sleeps: number,
    headlinePhoto: PropertyCardImagesShape,
    priceSummary: PriceSummaryShape,
    propertyMetadata: PropertyCardMetadataShape,
    starSize: string
};

type Props = {
    listing: PropertyCardListingShape,
    aspectRatio: '16x9' | '3x2' | '4x3' | '1x1' | '3x4',
    showTripBoardButton: boolean,
    isMobile: boolean,
    checkIn: string
};

const PropertyCard = (props: Props) => {
    const [entered, setEntered] = useState(false);
    const {
        listing,
        aspectRatio,
        showTripBoardButton,
        isMobile,
        checkIn
    } = props;
    const {
        detailPageUrl,
        listingId,
        averageRating,
        reviewCount,
        headlinePhoto,
        bedrooms,
        sleeps,
        priceSummary,
        propertyMetadata
    } = listing;
    const hasBedroomsOrSleeps = bedrooms || sleeps;

    listing.starSize = '12';

    const handeClick = () => {
        triggerSelectedSerpCarouselCardEvent(checkIn, listingId);
    };

    const handleWaypointEnter = () => {
        if (!entered) {
            setEntered(true);
            triggerPresentedSerpCarouselCardEvent(listingId);
        }
    };

    return (
        <Waypoint onEnter={handleWaypointEnter}>
            <div className="PropertyCard" onClick={handeClick}>
                {showTripBoardButton && (
                    <div className="PropertyCard__tripboardButton">
                        <TripBoardButton
                            expandedButton={false}
                            listingId={listingId}
                            averageRating={averageRating}
                            reviewCount={reviewCount}
                            tabIndex={-1}
                            thumbnailUrl={headlinePhoto && headlinePhoto.uri}
                            useNativeEventListener={isMobile}
                            redOnHover
                            actionlocation={'search'}
                        />
                    </div>
                )}
                <Link
                    className="PropertyCard__anchor"
                    href={detailPageUrl}
                    target="_blank"
                >
                    <AspectRatioCard aspectRatio={aspectRatio} hasHover={false}>
                        <AspectRatioCardImage
                            data-slot="figure"
                            alt={propertyMetadata && propertyMetadata.headline}
                            imageUri={headlinePhoto && headlinePhoto.uri}
                        />
                        <div
                            className="PropertyCard__content"
                            data-slot="content"
                        >
                            {!!hasBedroomsOrSleeps && (
                                <div className="PropertyCard__details">
                                    {!!bedrooms && (
                                        <span>
                                            <FormattedMessage
                                                id="bedroomsAbbreviationParam"
                                                description="bedrooms abbreviation label"
                                                defaultMessage="BR"
                                                values={{
                                                    bedrooms
                                                }}
                                            >
                                                {(txt: Node): Node => txt}
                                            </FormattedMessage>
                                        </span>
                                    )}
                                    {!!sleeps && (
                                        <span>
                                            <FormattedMessage
                                                id="sleepsAbbreviation"
                                                description="sleeps label"
                                                defaultMessage="Sleeps {value}"
                                                values={{
                                                    value: sleeps
                                                }}
                                            >
                                                {(txt: Node): Node => txt}
                                            </FormattedMessage>
                                        </span>
                                    )}
                                </div>
                            )}
                            {!!priceSummary && (
                                <div className="PropertyCard__price">
                                    <PriceSummary priceSummary={priceSummary} />
                                </div>
                            )}
                            {!!reviewCount && (
                                <div className="PropertyCard__starRating">
                                    <Rating listing={listing} isDualPrice />
                                </div>
                            )}
                        </div>
                    </AspectRatioCard>
                </Link>
            </div>
        </Waypoint>
    );
};

PropertyCard.defaultProps = {
    listing: {},
    aspectRatio: '3x2',
    showTripBoardButton: true
};

export default PropertyCard;
